"use client";

import Image from "next/image";
import Link from "next/link";
import React, { useState } from "react";

interface BlogCardProps {
    image: string;
    date: string;
    category: string;
    title: string;
    excerpt: string;
    author?: string;
    quoteMode?: boolean;
    slug?: string;
}

const BlogCardHero: React.FC<BlogCardProps> = ({
    image,
    date,
    category,
    title,
    excerpt,
    author,
    quoteMode = false,
    slug = "blog-post",
}) => {
    const [imageError, setImageError] = useState(false);
    const showPlaceholder = !image || image.trim() === '' || imageError;

    return (
        <Link href={`/blogs/${encodeURIComponent(slug || title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''))}`} className="block group">
            <div className="w-full overflow-hidden transition-all duration-300 hover:scale-[1.02] shadow-md">
                {/* Top Image */}
                <div className="relative w-full aspect-video overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
                    {showPlaceholder ? (
                        <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                            <div className="text-center">
                                <div className="text-6xl font-bold text-blue-600 mb-4">{title.charAt(0).toUpperCase()}</div>
                                <div className="text-sm text-blue-500 font-medium">Featured Article</div>
                            </div>
                        </div>
                    ) : (
                        <Image
                            src={image}
                            alt="Blog thumbnail"
                            fill
                            className="object-cover transition-transform duration-500"
                            quality={100}
                            onError={() => setImageError(true)}
                        />
                    )}
                    {/* Overlay effect */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300"></div>
                    {category && category.trim() !== '' && category.toLowerCase() !== 'blog' && category.toLowerCase() !== 'general' && category.toLowerCase() !== 'blog post' && (
                        <span className="absolute top-4 right-4 p-1 px-2 z-[60] bg-black/32 backdrop-blur-xl rounded-md border border-white/12 text-white text-sm  transition-all duration-300  group-hover:scale-105">
                            {category}
                        </span>
                    )}
                </div>

                {/* Content Card */}
                <div className="p-4 sm:py-9 sm:px-6">
                    {quoteMode ? (
                        <div className="bg-white  rounded-xl p-6 md:p-8 shadow-[0_8px_24px_rgba(0,0,0,0.08)] border border-[#ececec] transition-all duration-300 group-hover:shadow-[0_12px_40px_rgba(0,0,0,0.15)] group-hover:bg-gray-50">
                            <div className="flex items-start gap-4">
                                {/* Quote icon */}
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="mt-1 text-[#E0301E] transition-all duration-300 group-hover:scale-110"><path d="M7.17 6C5.97 6 5 6.97 5 8.17v3.66C5 13.03 5.97 14 7.17 14H9v4H5.83C4.27 18 3 16.73 3 15.17V8.83C3 7.27 4.27 6 5.83 6H7.17ZM18.17 6C16.97 6 16 6.97 16 8.17v3.66C16 13.03 16.97 14 18.17 14H20v4h-3.17C15.27 18 14 16.73 14 15.17V8.83C14 7.27 15.27 6 16.83 6h1.34Z" fill="currentColor" /></svg>
                                <p className="text-[#1b1b1b] italic leading-relaxed transition-colors duration-300 group-hover:text-[#333]">
                                    {excerpt}
                                </p>
                            </div>
                            {author && (
                                <div className="mt-4 text-[#E0301E] font-semibold transition-all duration-300 group-hover:scale-105">{author}</div>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className="flex justify-between items-center text-sm sm:text-base">
                                <span className="font-semibold text-[#1b1b1b] transition-colors duration-300 group-hover:text-[#272D57]">
                                    {date}
                                </span>
                             
                            </div>

                            <h3 className="text-[#272D57] text-sm sm:text-md lg:text-xl line-clamp-3 mt-2 font-semibold leading-tight mb-2 transition-all duration-300 group-hover:text-[#E0301E]">
                                {title}
                            </h3>

                            <p className="text-[#1b1b1b] text-sm lg:text-base leading-relaxed line-clamp-2 sm:line-clamp-3 transition-colors duration-300 group-hover:text-[#333]">
                                {excerpt}
                            </p>
                        </>
                    )}
                </div>
            </div>
        </Link>
    );
};

export default BlogCardHero;
